import type { AppState, Appointment, Business, BusinessSettings, Client, Service, StaffMember, Subscription, User, UserRole } from "@/lib/types";

interface SupabaseRows {
  profiles: any[];
  businesses: any[];
  services: any[];
  staff_members: any[];
  clients: any[];
  appointments: any[];
  business_settings: any[];
  subscriptions?: any[];
}

export function mapRowsToState(rows: SupabaseRows): AppState {
  const users: User[] = (rows.profiles ?? []).map((row) => ({
    id: row.id,
    role: row.role as UserRole,
    email: row.email,
    displayName: row.display_name,
    isActive: row.is_active ?? true,
    welcomeEmailSentAt: row.welcome_email_sent_at ?? null,
    businessId: row.business_id ?? undefined,
    clientId: row.client_id ?? undefined,
  }));

  const businesses: Business[] = (rows.businesses ?? []).map((row) => ({
    id: row.id,
    ownerUserId: row.owner_user_id,
    name: row.name,
    email: row.email ?? "",
    phone: row.phone ?? "",
    address: row.address ?? "",
    isActive: row.is_active ?? true,
  }));

  const services: Service[] = (rows.services ?? []).map((row) => ({
    id: row.id,
    businessId: row.business_id,
    name: row.name,
    category: row.category,
    duration: row.duration,
    price: row.price,
    active: row.active,
    description: row.description ?? "",
  }));

  const staff: StaffMember[] = (rows.staff_members ?? []).map((row) => ({
    id: row.id,
    businessId: row.business_id,
    email: row.email ?? "",
    userId: row.user_id ?? null,
    name: row.name,
    role: row.role,
    commissionRate: Number(row.commission_rate ?? 0),
    availability: row.availability ?? "",
    specialties: row.specialties ?? [],
    isClockedIn: row.is_clocked_in ?? false,
    clockedInAt: row.clocked_in_at ?? null,
  }));

  const clients: Client[] = (rows.clients ?? []).map((row) => ({
    id: row.id,
    businessId: row.business_id,
    name: row.name,
    email: row.email,
    phone: row.phone ?? "",
    notes: row.notes ?? "",
    lastVisit: row.last_visit ?? null,
    totalBookings: row.total_bookings ?? 0,
  }));

  const appointments: Appointment[] = (rows.appointments ?? []).map((row) => ({
    id: row.id,
    businessId: row.business_id,
    clientId: row.client_id,
    serviceId: row.service_id,
    staffId: row.staff_id,
    date: row.date,
    startMinutes: row.start_minutes,
    duration: row.duration,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));

  const settings: BusinessSettings[] = (rows.business_settings ?? []).map((row) => ({
    businessId: row.business_id,
    cancellationWindowHours: row.cancellation_window_hours,
    requireDeposit: row.require_deposit,
    emailReminders: row.email_reminders,
    smsReminders: row.sms_reminders,
    hours: row.hours,
  }));

  const subscriptions: Subscription[] = (rows.subscriptions ?? []).map((row) => ({
    id: row.id,
    businessId: row.business_id,
    planName: row.plan_name,
    status: row.status,
    amountCents: row.amount_cents ?? 0,
    billingInterval: row.billing_interval,
    provider: row.provider ?? "",
    providerCustomerId: row.provider_customer_id ?? "",
    nextBillingAt: row.next_billing_at ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));

  return { users, businesses, services, staff, clients, appointments, settings, subscriptions };
}
